import App from "../App";
import { Howl } from "howler";
import LocalStorageVars from "../../src/const/LocalStorageVars";
import LocalData from "./LocalData";
import GameModel from "../../src/model/GameModel";
import { Tween } from "./../Tween";
import SoundVars from "../const/SoundVars";
import GameEvents from "../const/GameEvents";
import ConsoleUtil from "./ConsoleUtil";
import GameLoader from "./GameLoader";
import ClipboardUtils from "./ClipboardUtils";
import * as packageJson from "./../../../../package.json";
import { Loader } from "pixi.js";
import StateMachine from "../states/StateMachine";
import AudioSequencer from "./AudioSequencer";
import InstanceFactory from "../factories/InstanceFactory";
import IMusic from "./IMusic";
import { GameHowl } from "./GameHowl";
import Utils from "./Utils";

/**
 * Provides methods for working with sound and music.
 */
class SoundController {
    protected soundBlobs: Map<string, ISoundBlob>;
    protected defaultMusicFadeTime: number;
    protected musicFadeTime: number;
    protected soundEnabled: boolean;
    protected musicEnabled: boolean;
    protected effectsEnabled: boolean;
    protected musicVol: number;
    protected masterVol: number;
    protected effectsVol: number;
    protected music: GameHowl;
    protected startMusicCall: Tween;
    private backgroundMusics: Map<string, IMusic>;
    public musicRequested: boolean = false;
    public howler: HowlerGlobal;
    public soundsLoaded: boolean = false;
    public currentSounds: GameHowl[] = [];

    private warningsShown: Map<string, boolean> = new Map<string, boolean>();
    
    private analyserLeft: AnalyserNode;
    private analyserRight: AnalyserNode;
    private minDecibels: number = -60;
    private isLinearVU: boolean = false;
    private bufferLeft: Uint8Array;
    private bufferRight: Uint8Array;
    private vuLeft: HTMLDivElement;
    private vuRight: HTMLDivElement;
    private defaultLatencyInMs: number = 200;

    private vuMeterSettings = {
        "linear": {
            "lime": [0, 70],
            "yellow": [70, 98],
            "red": [98, 100]
        },
        "logarithmic": {
            "lime": [0, 66.6],
            "yellow": [66.6, 85],
            "orange": [85, 99],
            "red": [99, 100]
        }
    };

    private sliderInitialColor = "#858585";
    private sliderStartColor = "#82CFD0";
    private sliderPlayingColor = "#00ff00e6";
    private sliderEndColor = "#fff";
    private saveMixerSlidersToLocalStorage: boolean;
    
    private autoFadeConfig: IAutoFadeConfiguration;
    private sequencers: AudioSequencer[];

    constructor() {
        this.backgroundMusics = new Map();
        this.sequencers = [];
    }

    public init() {
        this.soundBlobs = new Map<string, ISoundBlob>();
        this.defaultMusicFadeTime =
            GameModel.gameConfig.defaultMusicFadeTime || 0.3;
        // this.soundEnabled = LocalData.getBooleanValue(LocalStorageVars.SOUND_ENABLED);
        this.soundEnabled = true;
        this.musicEnabled = true;
        // this.musicEnabled = LocalData.getBooleanValue(LocalStorageVars.MUSIC_ENABLED);
        this.effectsEnabled = true;
        // this.effectsEnabled = LocalData.getBooleanValue(LocalStorageVars.EFFECTS_ENABLED);
        // this.masterVol = LocalData.getNumber(LocalStorageVars.MASTER_VOLUME) || 1; // Why was it removed ????
        // this.masterVol = 1;
        // this.musicVol = LocalData.getNumber(LocalStorageVars.MUSIC_VOLUME) || 1;
        this.musicVol = 1;
        // this.effectsVol = LocalData.getNumber(LocalStorageVars.EFFECTS_VOLUME) || 1;
        this.effectsVol = 1;
        this.howler = Howler;
        let controller = this;
        this.saveMixerSlidersToLocalStorage = App.config.saveMixerSlidersToLocalStorage || false;

        let interv = setInterval(() => {
            if ((Howler as any)._audioUnlocked) {
                ConsoleUtil.log("audio unlocked");
                App.dispatcher.emit(GameEvents.AUDIO_UNLOCKED);

                clearInterval(interv);
                this.initFadeSettings();                
                if (process.env.NODE_ENV !== 'production' && GameModel.urlParams.showMixer === 'true') {
                    controller.createSoundMixer();

                    controller.analyserLeft = Howler.ctx.createAnalyser();
                    controller.analyserRight = Howler.ctx.createAnalyser();
                    
                    controller.analyserLeft.minDecibels = controller.minDecibels;
                    controller.analyserRight.minDecibels = controller.minDecibels;

                    controller.analyserLeft.channelCount = 1;
                    controller.analyserLeft.channelCountMode = "explicit";

                    controller.analyserRight.channelCount = 1;
                    controller.analyserRight.channelCountMode = "explicit";
                    
                    var splitter = Howler.ctx.createChannelSplitter(2);
                    splitter.channelCount = 2;
                    splitter.channelCountMode = "explicit";
                    splitter.connect(controller.analyserLeft, 0, 0);
                    splitter.connect(controller.analyserRight, 1, 0);
                    Howler.masterGain.connect(splitter);

                    controller.bufferLeft = new Uint8Array(controller.analyserLeft.frequencyBinCount);
                    controller.bufferRight = new Uint8Array(controller.analyserRight.frequencyBinCount);
                }
            }
        }, 50);
        this.toggleVisibilitychangeEvent();
    }

    /**
     * Switched on or off game music when game tab is lose focus
     */
    private toggleMusicWhenVisibilityChange = (): void => {
        if (document.visibilityState == "hidden") {
            this.howler.mute(true);
        } else {
            this.howler.mute( this.isEffectsEnabled || this.isMusicEnabled ? false : true);
        }
    }

    /**
     * Add or remove document "visibilitychange" event for switch on or off music when game tab is lose focus
     * @param add - if not set param, default value is *"true"* (add listener)
     */
    public toggleVisibilitychangeEvent(add: boolean = true): void {
        if (add) {
            document.addEventListener("visibilitychange", this.toggleMusicWhenVisibilityChange);
        } else {
            document.removeEventListener("visibilitychange", this.toggleMusicWhenVisibilityChange);
        }
    }

    public toggleMasterSound(state?: any) {
        if (this.isEffectsEnabled && this.isMusicEnabled) {
            this.toggleEffects();
            this.toggleMusic();
            this.toggleSound(false);
        } else if (!this.isEffectsEnabled && this.isMusicEnabled) {
            this.toggleMusic();
            this.toggleSound(false);
        } else if (this.isEffectsEnabled && !this.isMusicEnabled) {
            this.toggleEffects();
            this.toggleSound(false);
        } else if (!this.isEffectsEnabled && !this.isMusicEnabled) {
            this.toggleEffects();
            this.toggleMusic();
            this.toggleSound(true);
     }
}

    /**
     * Global sound toggle
     * @param state
     */
    public toggleSound(state?: boolean) {
        this.soundEnabled = state != undefined ? state : !this.soundEnabled;
        if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
            ConsoleUtil.log("[SoundController] Toggle Sound: " + this.soundEnabled);
        }
        this.howler.mute(!this.soundEnabled);
        LocalData.saveValue(LocalStorageVars.SOUND_ENABLED, this.soundEnabled);
        if (this.soundEnabled) {
            this.masterVolume =
                LocalData.getNumber(LocalStorageVars.MASTER_VOLUME) || 1;
            App.dispatcher.emit(GameEvents.SOUNDS_UNMUTED);
        } else {
            this.masterVolume = 0;
            App.dispatcher.emit(GameEvents.SOUNDS_MUTED);
        }
        return this.soundEnabled;
    }

    /**
     * Music sound toggle. If soundEnabled is set to false. This will have no effect
     */
    public toggleMusic(state?: boolean) {
        this.musicEnabled = state || !this.musicEnabled;
        if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
            ConsoleUtil.log("[SoundController] Toggle Music: " + this.musicEnabled);
        }
        LocalData.saveValue(LocalStorageVars.MUSIC_ENABLED, this.musicEnabled);

        Array.from(this.backgroundMusics.keys()).forEach((name) => {
            let music = this.backgroundMusics.get(name);
            if (this.musicEnabled) {
                music.isMuted = false;
                music.snd.volume(music.saveVolume);
            } else {
                music.isMuted = true;
                music.snd.volume(0);
            }
        });
        
        this.toggleSound(this.musicEnabled || this.effectsEnabled);
        
        return this.musicEnabled;
    }

    public fadeMusic(from: number, to: number, duration: number, musicIds?: Array<string>) {
        let iterableEl = (musicIds && musicIds.length) ? musicIds : Array.from(this.backgroundMusics.keys());
        iterableEl.forEach((name) => {
            if(this.backgroundMusics.has(name)) {
                let music = this.backgroundMusics.get(name);
                if(this.musicEnabled) {
                    music.snd.fade(from, to, duration * 1000);
                }
                music.saveVolume = to;
            }
        });
    }

    public toggleEffects(state?: boolean) {
        this.effectsEnabled = state || !this.effectsEnabled;
        if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
            ConsoleUtil.log(
                "[SoundController] Toggle Effects: " + this.effectsEnabled
            );
        }
        if (this.effectsEnabled) {
            this.effectsVol = 1;
        } else {
            this.effectsVol = 0;
        }
        LocalData.saveValue(
            LocalStorageVars.EFFECTS_ENABLED,
            this.effectsEnabled
        );
        
        for (let i = 0; i < this.currentSounds.length; i++) {
            const soundName = this.currentSounds[i].name;
            if (this.currentSounds[i] != (this.backgroundMusics.has(soundName.toString())
                                          && this.backgroundMusics.get(soundName.toString()).snd)
            ) {
                this.currentSounds[i].mute(!this.effectsEnabled);
            }
        }
        this.toggleSound(this.musicEnabled || this.effectsEnabled);
    }

    get isSoundEnabled() {
        return this.soundEnabled;
    }

    get isMusicEnabled() {
        return this.musicEnabled;
    }

    get isEffectsEnabled() {
        return this.effectsEnabled;
    }

    get masterVolume() {
        if (
            this.masterVol === undefined ||
            this.masterVol === null ||
            isNaN(this.masterVol)
        ) {
            this.masterVol = this.soundEnabled ? 1 : 0;
        }
        return this.masterVol;
    }

    get effectsVolume() {
        return this.effectsVol;
    }

    get musicVolume() {
        return this.musicVol;
    }

    set masterVolume(volume: number) {
        if (this.masterVol === volume) {
            return;
        }
        this.masterVol = volume;
        LocalData.saveValue(LocalStorageVars.MASTER_VOLUME, volume);
        this.howler.volume(this.masterVol);

        let soundEnabled = false;
        if (this.masterVolume > 0) {
            soundEnabled = true;
        } else {
            soundEnabled = false;
        }
        LocalData.saveValue(LocalStorageVars.MASTER_VOLUME, this.masterVol);
        if (soundEnabled != this.soundEnabled) {
            this.toggleMasterSound({ data: { value: soundEnabled } });
            this.howler.mute(!this.soundEnabled);
        }
    }

    set effectsVolume(volume: number) {
        this.effectsVol = volume;
        LocalData.saveValue(LocalStorageVars.EFFECTS_VOLUME, volume);
        for (let i = 0; i < this.currentSounds.length; i++) {
            const currentSound = this.currentSounds[i];
            currentSound.volume(this.effectsVol);
        }
    }

    set musicVolume(volume: number) {
        this.musicVol = volume;
        LocalData.saveValue(LocalStorageVars.MUSIC_VOLUME, volume);
        Array.from(this.backgroundMusics.keys()).forEach((name) => {
            let music = this.backgroundMusics.get(name);
            music.snd.volume(this.musicVol);
            music.saveVolume = this.musicVol;
        });
    }

    public setSpecificMusicVolume(musicName:string, volume:number) {
        if(this.backgroundMusics.has(musicName)) {
            let music = this.backgroundMusics.get(musicName);
            music.snd.volume(volume);
            music.saveVolume = volume;
        }
    }

    public getSpecificMusicVolume(musicName: string): number {
        if(this.backgroundMusics.has(musicName)) {
            let music = this.backgroundMusics.get(musicName);
            return music.snd.volume();
        }

        return Number.NaN;
    }

    public playSound(
        sndId: string,
        loop = false,
        sprites?: IHowlSoundSpriteDefinition,
        alowDuplicate = true,
        pan: number = 0
    ) {
        if (!sndId) {
            return;
        }

        if (!this.soundEnabled || !this.effectsEnabled) {
            return;
        }

        if (!alowDuplicate && this.soundIsPlaying(sndId)) {
            return;
        }

        const snd: GameHowl = this.getSound(
            sndId,
            loop,
            this.effectsVol,
            sprites
        );

        if ((<any>snd)._src == '') {
            if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
                ConsoleUtil.red("[SoundController] Try to play non existent sound " + sndId);
            }
            return snd;
        }
        if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
            ConsoleUtil.log("[SoundController] Play sound: " + sndId);
        }
        let volumeOverride = App.config.soundVolumeOverride?.[sndId];
        if (Utils.isNumeric(volumeOverride)) {
            if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
                ConsoleUtil.log(
                    "[SoundController] Override volume:",
                    volumeOverride
                );
            }
            snd.volume(volumeOverride);
        }

        if (pan != 0) {
            if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
                ConsoleUtil.log(
                    `[SoundController] ${sndId} pan: ${pan}`
                );
            }

            snd.pos(pan, 0, 0);
        }

        snd.play();
        if (loop == false) {
            snd.once("end", () => {
                this.unloadSound(snd);
            });
        }

        this.autoFadeSoundTriggers(snd);
        return snd;
    }

    public stopSound(sound: Howl): void;
    public stopSound(soundName: string): void;
    public stopSound(soundOrName: Howl | string): void {
        let sound: Howl = undefined;
        if (soundOrName instanceof Howl) {
            sound = soundOrName;
        } else {
            sound = this.currentSounds?.find(s => s.name == soundOrName);
        }

        if (sound) {
            sound.stop();
            sound.off();
            this.unloadSound(sound);
        }
    }

    public fadeStop(sound: Howl, duration: number): void;
    public fadeStop(soundName: string, duration: number): void;
    public fadeStop(soundOrName: Howl | string, duration: number): void {
        let sound: Howl = undefined;
        if (soundOrName instanceof Howl) {
            sound = soundOrName;
        } else {
            sound = this.currentSounds?.find(s => s.name == soundOrName);
        }

        if (sound) {
            this.fadeSound(sound, sound.volume(), 0, duration, () => {
                setTimeout((() => {
                    this.stopSound(sound);
                }).bind(this), this.defaultLatencyInMs);
            });
        }
    }

    public fadeSound(
        sound: Howl,
        from: number,
        to: number,
        duration: number,
        onComplete?: () => void
    ) {
        if (onComplete) {
            sound.once("fade", () => {
                onComplete();
            });
        }
        sound.fade(from, to, duration * 1000);
    }

    /**
     * Play music
     * @param musicId
     * @param seek
     * @param seamless - if added uses gameconfig's musicTempTime to switch the sound at the start of the beat
     * @param seamlessMusicIds - list of music to stop
     * @param alowDuplicate - play two or more sounds at the same time
    */
    playMusic(musicId: string, seek: number = -1, seamless = false, seamlessMusicIds = [], alowDuplicate = false) {
        if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
            ConsoleUtil.log("[SoundController] Play music: " + musicId);
        }
        if (this.backgroundMusics.has(musicId.toString())) {
            if (seamless) {
                const seekTime = this.backgroundMusics.get(musicId.toString()).snd.seek() as number;
                const delay = seekTime % GameModel.gameConfig.musicTempTime;
                const iterator = seamlessMusicIds || Array.from(this.backgroundMusics.keys());
                iterator.forEach((name) => {
                    if(this.backgroundMusics.has(name)) {
                        let music = this.backgroundMusics.get(name);
                        music.snd.fade(1, 0, delay * 1000);
                        music.saveVolume = 0;
                    }
                });
                switch (musicId) {
                    case SoundVars.MUSIC_MAIN:
                        this.playSound(SoundVars.EFFECT_SWITCH_TO_MAIN);
                        break;
                    case SoundVars.MUSIC_MAIN2:
                    case SoundVars.MUSIC_MAIN3:
                    case SoundVars.MUSIC_BONUS:
                        this.playSound(SoundVars.EFFECT_SWITCH_TO_NEXT);
                        break;
                }
                Tween.delayedCall(
                    delay,
                    () => {
                        this.stopMusic(seamlessMusicIds);
                        this.startMusic(musicId, 0, alowDuplicate);
                        let music = this.backgroundMusics.get(musicId.toString());
                        music.snd.fade(0, this.musicVol, 200);
                        music.saveVolume = this.musicVol;
                    },
                    null,
                    this
                );
            } else {
                if(!alowDuplicate) {
                    this.stopMusic();
                }  
                this.startMusic(musicId, seek, alowDuplicate);
                let music = this.backgroundMusics.get(musicId.toString());
                /*music.snd.fade(0, this.musicVol, 1);
                music.saveVolume = this.musicVol;*/
            }
        } else {
            this.startMusic(musicId, seek, alowDuplicate);
        }
    }

    private startMusic(musicId: string, seek: number = -1, alowDuplicate): void {
        if (!this.backgroundMusics.has(musicId.toString())) {
            const sequencer = this.sequencers.find(seq => seq.name == musicId);
            let musicObj: IMusic;
            if (!sequencer) {
                musicObj = {
                    "name": musicId.toString(),
                    "isActive": false,
                    "isMuted": false,
                    "saveVolume": this.musicVol,
                    "snd": this.getSound(musicId, true, this.musicVol)
                };
            } else {
                sequencer.loop = true;
                sequencer.volume = this.musicVol;
                musicObj = sequencer.getSequence();
                musicObj.isActive = false;
                musicObj.isMuted = false;
                musicObj.saveVolume = this.musicVol;
            }

            this.backgroundMusics.set(musicId.toString(), musicObj);
        } 

        let music = this.backgroundMusics.get(musicId.toString());
        if(!music.isActive ) {
            if(!alowDuplicate) {
                this.stopMusic();
            }
            music.snd.play();
            music.saveVolume = this.musicVol;
            music.snd.volume(this.musicVol);
            let volumeOverride = App.config.soundVolumeOverride?.[musicId.toString()];
            if (Utils.isNumeric(volumeOverride)) {
                if (!GameModel.gameConfig.debugLogging || GameModel.gameConfig.debugLogging.soundController === true) {
                    ConsoleUtil.log(
                        "[SoundController] Override volume:",
                        volumeOverride
                    );
                }
                music.saveVolume = volumeOverride;
                music.snd.volume(volumeOverride);
            }
            music.isActive = true;
            if (!this.musicEnabled) {
                music.isMuted = true;
                music.snd.volume(0);
            }
            
            if (seek != -1) {
                music.snd.seek(seek);
            }
        }
    }

    public stopMusic(musicIds?:Array<string>): void {
        let iterableEl = (musicIds && musicIds.length) ? musicIds : Array.from(this.backgroundMusics.keys());
        iterableEl.forEach((name) => {
            if(this.backgroundMusics.has(name)) {
                let music = this.backgroundMusics.get(name);
                music.snd.pause();
                music.isActive = false;
            }
        });
    }

    public resumeMusic(musicIds?:Array<string>): void {
        let iterableEl = (musicIds && musicIds.length) ? musicIds : Array.from(this.backgroundMusics.keys());
        iterableEl.forEach((name) => {
            if(this.backgroundMusics.has(name)) {
                let music = this.backgroundMusics.get(name);
                music.snd.play();
                music.isActive = true;
                if(!this.musicEnabled) {
                    music.isMuted = true;
                    music.snd.volume(0);
                }
            }
        });
    }

    public isMusicPlaying(musicId?: string): boolean {
        if(musicId) {
            return this.backgroundMusics.has(musicId);
        }
        return this.backgroundMusics.size > 0;
    }

    public soundIsPlaying(soundId: string): boolean {
        let soundPlaying = false;
        if (this.currentSounds) {
            this.currentSounds.forEach((snd) => {
                if (snd.name === soundId) {
                    soundPlaying = true;
                }
            });
        }

        return soundPlaying;
    }

    public soundExists(soundId: string) {
        const soundBlob = this.soundBlobs.get(soundId);
        if (!soundBlob) {
            return false;
        }

        if (soundBlob.url == '') {
            return false;
        }

        return true;
    }

    /**
     * Get a raw sound
     * @param soundId
     * @param loop
     * @param volume
     * @param sprites
     * @returns {*|GameHowl}
     */
    protected getSound(
        soundId: string,
        loop = false,
        volume = 1,
        sprites?: IHowlSoundSpriteDefinition
    ): GameHowl {
        const sequencer = this.sequencers.find(seq => seq.name == soundId);
        if (sequencer) {
            sequencer.loop = loop;
            sequencer.volume = volume;
            this.currentSounds.push(sequencer.currentSound);
            return sequencer.currentSound;
        }

        let blobUrl: string | ISoundBlob | undefined =
            this.soundBlobs.get(soundId);

        if (blobUrl == undefined) {
            if (!soundId || !this.warningsShown.get(soundId.toString())) {
                ConsoleUtil.warn(
                    "Could not find data for sound with id : " + soundId
                );
                this.warningsShown.set(soundId ? soundId.toString() : "", true);
            }

            const sound = new GameHowl({
                src: [""],
                format: ["m4a"],
                loop,
                preload: true,
                volume,
                sprite: sprites ? sprites : undefined,
                name: soundId,
            });

            if (this.soundBlobs.has(soundId) != undefined) {
                this.currentSounds.push(sound);
            }

            return sound;
        }

        const soundBlob = this.soundBlobs.get(soundId);

        const snd = new GameHowl({
            src: [soundBlob.url],
            format: [soundBlob.extension],
            loop,
            preload: true,
            volume,
            sprite: sprites ? sprites : undefined,
            name: soundId,
        });

        if (this.soundBlobs.has(soundId)) {
            this.currentSounds.push(snd);
        }
        return snd;
    }

    public hasSound(sndId: string): boolean {
        return this.soundBlobs.has(sndId);
    }

    public async loadSounds(soundList: string[][]) {
        for (let j = 0; j < soundList.length; j++) {
            const sndId = soundList[j][0];
            if (Array.isArray(soundList[j][1])) {
                // We have a multitrack music/sound.
                const tracks: GameHowl[] = [];
                const arr = Array.from(soundList[j][1]);
                for (let index = 0; index < arr.length; index++) {
                    const trackId = `${sndId}~track~${index}`;
                    const track = await this.loadSingleSound(trackId, true);
                    tracks.push(track);
                }

                const sequencerClassName = soundList[j][2];
                if (!sequencerClassName) {
                    throw new Error(`No audio sequencer was provided to multitrack with name ${sndId}. Check asset-config.json.`);
                }

                const sequencer = InstanceFactory.getClass(sequencerClassName);
                if (!(sequencer instanceof AudioSequencer)) {
                    throw new Error(`Invalid audio sequencer was provided to multitrack with name ${sndId}. The class must extends AudioSequencer abstract class! Check asset-config.json.`);
                }

                const bpm = Number(soundList[j][3]);
                const bpsMultiplier = Number(soundList[j][4]);
                await sequencer.init(sndId, tracks, bpm, bpsMultiplier);
                this.sequencers.push(sequencer);

            } else {
                await this.loadSingleSound(sndId, false);
            }
        }

        this.soundsLoaded = true;
    }

    private async loadSingleSound(sndId: string, setBufferProperty: boolean): Promise<GameHowl> {
        return new Promise<GameHowl>(resolve => {
            let blobUrl: string | ISoundBlob | undefined =
            this.soundBlobs.get(sndId);
            if (blobUrl == undefined) {
                const byteArray = Loader.shared.resources[sndId];
                const format = byteArray.data?.type?.split("/")[1];
                if (byteArray && byteArray.data) {
                    const blob = new Blob([byteArray.data], {
                        type: byteArray.data.type,
                    });
                    blobUrl = window.URL.createObjectURL(blob);
                    const soundBlob: ISoundBlob = {
                        url: blobUrl,
                        extension: format,
                    };
    
                    this.soundBlobs.set(sndId, soundBlob);
                    const snd = new GameHowl({
                        src: [soundBlob.url],
                        format: [soundBlob.extension],
                        preload: true,
                        name: sndId
                    });
    
                    if (setBufferProperty) {
                        const fileReader = new FileReader();
                        fileReader.addEventListener("loadend", async () => {
                            snd.buffer = await this.howler.ctx.decodeAudioData(fileReader.result as ArrayBuffer);
                            resolve(snd);
                        });
        
                        fileReader.readAsArrayBuffer(blob);
                    } else {
                        resolve(snd);
                    }
                }
            }
        });
    }

    public unloadSound(snd: Howl) {
        const index = this.currentSounds.indexOf(snd as GameHowl);
        if (index > -1) {
            this.currentSounds.splice(index, 1);
        }

        // Checking if just unloaded the last sound instance with this id.
        const soundName = (snd as GameHowl).name;
        const target = this.currentSounds.find(x => x.name == soundName);
        if (!target) {
            App.dispatcher.emit("AllSoundInstancesUnloaded_" + soundName);
        }

        snd.unload();
    }

    private createSoundMixer() {
        let controller = this;

        let mixer = document.createElement("div");
        mixer.style.cssText = 
        `
        position: absolute;
        top: 0px;
        left: 0px;
        height: 99vh;
        min-height: 100px;
        min-width: 150px;
        background-color: rgba(40,59,59, 0.98);
        overflow: auto;
        color: #000;
        resize: both;
        padding: 5px;
        border-radius: 8px;
        `;

        mixer.id = "mixer";
        if (GameModel.isMobile(true)) {
            mixer.style.top = "0px";
            mixer.style.width = "20vw";
        }

        let header = document.createElement("div");
        header.style.height = "30px";

        let logo = document.createElement("span");
        logo.innerText = "Wiene® Volume Mixer";
        logo.style.cssText = "vertical-align: middle; height: 30px; color: white";

        let importButton = document.createElement("input");
        importButton.type = "button";
        importButton.value = "Import";
        importButton.style.cssText = 
        `
        width: 70px;
        height: 30px;
        diplay: block;
        position: absolute;
        top: 0px;
        right: 80px;
        margin: 5px;
        opacity: 0.8;
        border-radius: 8px;
        `;
        
        let exportButton = document.createElement("input");
        exportButton.type = "button";
        exportButton.value = "Export";
        exportButton.style.cssText = 
        `
        width: 70px;
        height: 30px;
        diplay: block;
        position: absolute;
        top: 0px;
        right: 0px;
        margin: 5px;
        opacity: 0.8;
        border-radius: 8px;
        `;

        importButton.onclick = function() {
            let input = prompt("Enter sound configuration");
            if (!input) {
                return;
            }

            let startIdx = input.indexOf("{");
            let endIdx = input.indexOf("}");
            if (startIdx == -1 || endIdx == -1) {
                alert("Invalid sound configuration!");
                return;
            }

            try {
                // Parsing the sound volume overrides.
                let data = input.substring(startIdx, endIdx + 1);
                let obj = JSON.parse(data);
                let keys = Object.keys(obj);
                if (keys.length == 0) {
                    alert("Empty sound configuration!");
                    return;
                }

                keys.forEach((key) => {
                    if (!controller.soundBlobs.has(key)) {
                        throw new Error(key + " is not a valid sound id!");
                    }

                    let volume = Number(obj[key]);
                    if (volume < 0 || volume > 1) {
                        throw new Error("Volume out of range! Sound id: " + key);
                    }
                });

                App.config.soundVolumeOverride = obj;
                controller.loadSoundOverrides();

                // Parsing the fade settings
                const fadeSettingsStartIndex = input.indexOf("{", endIdx + 1);
                const fadeSettingsEndIndex = input.lastIndexOf("}") + 1;
                if (fadeSettingsStartIndex != -1 && fadeSettingsEndIndex != -1) {
                    data = input.substring(fadeSettingsStartIndex, fadeSettingsEndIndex);
                    const fadeSettings = JSON.parse(data) as IAutoFadeConfiguration;
                    if (fadeSettings) {
                        App.config.fadeSettings = fadeSettings;
                        controller.initFadeSettings();
                    }
                }
            } catch (error) {
                alert("Import failed!\n" + error);
            }
        };

        exportButton.onclick = function() {
            let text = "```\n" + packageJson.name + " " + packageJson.version + " game-config.json" + "\n\"soundVolumeOverride\": ";
            text += JSON.stringify(App.config.soundVolumeOverride, null, 4);

            if (controller.autoFadeConfig) {
                text += ",\n";
                text += "\"fadeSettings\":"
                text += JSON.stringify(controller.autoFadeConfig, null, 4);
            } else {
                text += "\n";
            }

            text += "\n```";
            ClipboardUtils.copyToClipboard(text);
            alert("Sound configuration copied to clipboard!");
        };

        header.appendChild(logo);
        header.appendChild(importButton);
        header.appendChild(exportButton);
        mixer.appendChild(header);

        let vuWrapperLeft = document.createElement("div");
        vuWrapperLeft.style.cssText = "border: 1px solid black; vertical-align: middle; margin: 5px 0px 0px 0px; background: linear-gradient(90deg, rgba(0,255,0,0.15) 0%, rgba(0,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 85%, rgba(255,165,0,0.15) 85%, rgba(255,165,0,0.15) 99%, rgba(255,0,0,0.15) 99%, rgba(255,0,0,0.15) 100%);";
        let vuWrapperRight = document.createElement("div");
        vuWrapperRight.style.cssText = "border: 1px solid black; vertical-align: middle; margin: 0px 0px 5px 0px; background: linear-gradient(90deg, rgba(0,255,0,0.15) 0%, rgba(0,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 85%, rgba(255,165,0,0.15) 85%, rgba(255,165,0,0.15) 99%, rgba(255,0,0,0.15) 99%, rgba(255,0,0,0.15) 100%);";

        this.vuLeft = document.createElement("div");
        this.vuLeft.style.cssText = 
        `
        background-color: transparent;
        height: 12px;
        width: 100%;
        `;

        this.vuRight = document.createElement("div");
        this.vuRight.style.cssText = 
        `
        background-color: transparent;
        height: 12px;
        width: 100%;
        `;

        vuWrapperLeft.appendChild(this.vuLeft);
        vuWrapperRight.appendChild(this.vuRight);
        const dbSpanLeft = document.createElement("div");
        dbSpanLeft.innerHTML = `${controller.minDecibels}db`;
        dbSpanLeft.style.cssText = "vertical-align: middle; font-size: 12px;user-select:none; margin-left: 5px; height: 15px";
        this.vuLeft.appendChild(dbSpanLeft);
        mixer.appendChild(vuWrapperLeft);
        mixer.appendChild(vuWrapperRight);
        const toggleVuMeter = function() {
            controller.isLinearVU = !controller.isLinearVU;
            if (controller.isLinearVU) {
                dbSpanLeft.innerHTML = `0%`;
                vuWrapperLeft.style.cssText = "border: 1px solid black; vertical-align: middle; margin: 5px 0px 0px 0px; background: linear-gradient(90deg, rgba(0,255,0,0.15) 0%, rgba(0,255,0,0.15) 70%, rgba(255,165,0,0.15) 70%, rgba(255,165,0,0.15) 98%, rgba(255,0,0,0.15) 98%, rgba(255,0,0,0.15) 100%);";
                vuWrapperRight.style.cssText = "border: 1px solid black; vertical-align: middle; margin: 0px 0px 5px 0px; background: linear-gradient(90deg, rgba(0,255,0,0.15) 0%, rgba(0,255,0,0.15) 70%, rgba(255,165,0,0.15) 70%, rgba(255,165,0,0.15) 98%, rgba(255,0,0,0.15) 98%, rgba(255,0,0,0.15) 100%);";
            } else {
                dbSpanLeft.innerHTML = `${controller.minDecibels}db`;
                vuWrapperLeft.style.cssText = "border: 1px solid black; vertical-align: middle; margin: 5px 0px 0px 0px; background: linear-gradient(90deg, rgba(0,255,0,0.15) 0%, rgba(0,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 85%, rgba(255,165,0,0.15) 85%, rgba(255,165,0,0.15) 99%, rgba(255,0,0,0.15) 99%, rgba(255,0,0,0.15) 100%);";
                vuWrapperRight.style.cssText = "border: 1px solid black; vertical-align: middle; margin: 0px 0px 5px 0px; background: linear-gradient(90deg, rgba(0,255,0,0.15) 0%, rgba(0,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 66.6%, rgba(255,255,0,0.15) 85%, rgba(255,165,0,0.15) 85%, rgba(255,165,0,0.15) 99%, rgba(255,0,0,0.15) 99%, rgba(255,0,0,0.15) 100%)";
            }
        }

        vuWrapperLeft.onclick = toggleVuMeter;
        vuWrapperRight.onclick = toggleVuMeter;

        GameLoader.config.music.forEach(item => {
            mixer.appendChild(this.createMixerControl(item[0], true));
        });

        GameLoader.config.sounds.forEach(item => {
            mixer.appendChild(this.createMixerControl(item[0], false));
        });

        let dynamic = document.createElement("div");
        dynamic.innerText = "Dynamically loaded:";
        mixer.appendChild(dynamic);

        document.body.appendChild(mixer);
        App.instance.ticker.add((delta) => {
            this.updateMixer();
        })
    }

    private createMixerControl(soundName: string, isMusic: boolean) {
        let controller = this;
        let id = "snd_" + soundName;
        let control = document.createElement("div");

        let browseButtonHidden = document.createElement("input");
        browseButtonHidden.type = "file";
        browseButtonHidden.accept = ".m4a";
        browseButtonHidden.style.cssText =
        `
        display: none;
        `;

        browseButtonHidden.onchange = function(e) {
            let target = e.target as HTMLInputElement;
            if (target && target.files && target.files.length == 1) {
                let file = target.files[0];
                if (file.name.toLowerCase().endsWith("m4a")) {
                    let id = file.name.substring(0, file.name.length - 4);
                    // Restricted to the same id
                    if (soundName != id) {
                        alert(`You can not change the sound id!\nThe file name must be ${soundName}.m4a`);
                        return;
                    }

                    let url = window.URL.createObjectURL(file);
                    if (soundName != id && controller.soundBlobs.has(id)) {
                        alert("A sound with this id already exists! Id: " + id);
                        return;
                    }

                    const soundBlob: ISoundBlob = {
                        url: url,
                        extension: "m4a",
                    };

                    if (controller.soundBlobs.has(soundName)) {
                        controller.soundBlobs.delete(soundName);
                    }

                    controller.soundBlobs.set(id, soundBlob);
                    let control = controller.getMixerControl(soundName);
                    control.name.textContent = id;
                    control.volume.textContent = "1.00";
                    control.slider.id = "snd_" + id;
                    control.slider.value = "1.0";
                    delete App.config.soundVolumeOverride[soundName];
                    App.config.soundVolumeOverride[id] = 1.0;
                    soundName = id;
                    target.parentElement.style.border = "1px dashed lime";
                } else {
                    alert("Only *.m4a files are supported!");
                }
            }
        };

        let browseButtonReal = document.createElement("button");
        browseButtonReal.type = "button";
        browseButtonReal.innerHTML = "&#x266b;"
        browseButtonReal.style.cssText =
        `
        width: 30px;
        vertical-align: middle;
        border-radius: 8px;
        `;

        browseButtonReal.onclick = function() {
            browseButtonHidden.click();
        }

        let playButton = document.createElement("button");
        playButton.type = "button";
        playButton.innerHTML = "&#x25ba;";
        playButton.style.cssText =
        `
        width: 30px;
        vertical-align: middle;
        border-radius: 8px;
        `;

        playButton.disabled = isMusic;
        playButton.onclick = function() {
            if (controller.soundExists(soundName)) {
                controller.playSound(soundName)
            } else {
                playButton.style.color = "red";
            }
        };

        let downloadButton = document.createElement("button");
        downloadButton.type = "button";
        downloadButton.innerHTML = "&#11167;";
        downloadButton.style.cssText =
        `
        width: 30px;
        vertical-align: middle;
        border-radius: 8px;
        `;

        downloadButton.onclick = function() {
            let control = controller.getMixerControl(soundName);
            if (control && controller.soundBlobs.has(soundName)) {
                window.open(controller.soundBlobs.get(soundName).url);
            }
        };

        let slider = document.createElement("input");
        slider.id = id;
        slider.type = "range";
        slider.min = "0";
        slider.max = "1";
        slider.step = "0.05";
        slider.style.cssText =
        `
        background: linear-gradient(to right, ${this.sliderStartColor} 0%, ${this.sliderStartColor} 50%, ${this.sliderEndColor} 50%, ${this.sliderEndColor} 100%);
        border: solid 1px white;
        border-radius: 8px;
        vertical-align: middle;
        outline: none;
        transition: background 450ms ease-in;
        -webkit-appearance: none;
        opacity: 0.3
        `;

        if (!App.config.soundVolumeOverride) {
            App.config.soundVolumeOverride = {};
        }

        
        let span = document.createElement("span");
        span.style.margin = "5px";
        
        if(this.saveMixerSlidersToLocalStorage && LocalData.getValue(soundName)) {
            slider.value = LocalData.getValue(soundName);
            span.innerText = slider.value;
            App.config.soundVolumeOverride[soundName] = LocalData.getNumber(soundName);
        } else if (App.config.soundVolumeOverride[soundName]) {
            slider.value = App.config.soundVolumeOverride[soundName].toFixed(2);
            span.innerText = App.config.soundVolumeOverride[soundName].toFixed(2);
        } else {
            span.innerText = "1.00";
        }
        this.saveMixerSlidersToLocalStorage && LocalData.saveValue(soundName, slider.value);

        slider.onchange = function(ev) {
            let volume = Number(slider.value);
            let soundSource = controller.currentSounds.find(x => x.name == soundName);
            if (soundSource) {
                soundSource.volume(volume);
                if (controller.isMusic(soundName)) {
                    let maxMusicVolume = 0;
                    controller.backgroundMusics.forEach(bgMusic => {
                        maxMusicVolume = Math.max(bgMusic.snd.volume(), maxMusicVolume);
                    })

                    App.config.maxMusicVolume = maxMusicVolume;
                }
            }
            
            App.config.soundVolumeOverride[soundName] = volume;
            
            span.innerText = volume.toFixed(2);
            let color = controller.soundIsPlaying(soundName) ? controller.sliderPlayingColor : controller.sliderStartColor;
            if (!controller.soundExists(soundName)) {
                color = "red";
                slider.disabled = true;
            }

            controller.updateSliderStyle(
                slider,
                color,
                controller.sliderEndColor);
            
            controller.saveMixerSlidersToLocalStorage && LocalData.saveValue(soundName, slider.value);
        };

        let label = document.createElement("label");
        label.htmlFor = id;
        label.innerText = soundName;

        control.appendChild(browseButtonHidden);
        control.appendChild(browseButtonReal);
        control.appendChild(playButton);
        control.appendChild(downloadButton);
        control.appendChild(slider);
        control.appendChild(span);
        control.appendChild(label);
        return control;
    }

    private updateMixer() {
        this.currentSounds.forEach(sound => {
            let id = "snd_" + sound.name;
            let slider = document.getElementById(id) as HTMLInputElement;
            if (!slider) {
                // This is a dynamically loaded sound
                let control = this.createMixerControl(sound.name.toString(), false);
                document.getElementById("mixer").appendChild(control);
                slider = document.getElementById(id) as HTMLInputElement;
            }

            let volume = sound.volume().toFixed(2);
            slider.value = volume;
            slider.parentElement.querySelector("span").textContent = volume;
            slider.style.opacity = "1.0";
         });

        this.sequencers.forEach(sequencer => {
            const sequence = sequencer.getSequence();
            sequence.tracks.forEach(sound => {
                const id = "snd_" + sound.name;
                let slider = document.getElementById(id) as HTMLInputElement;
                if (!slider) {
                    // This is a sequencer track
                    const control = this.createMixerControl(sound.name.toString(), this.isMusic(sequence.name));
                    document.getElementById("mixer").appendChild(control);
                    slider = document.getElementById(id) as HTMLInputElement;
                }
    
                const volume = sound.volume().toFixed(2);
                const timeLeft = sound.duration() - (sound.seek() as number);
                slider.value = volume;
                slider.parentElement.querySelector("span").textContent = volume;
                slider.parentElement.querySelector("label").textContent = `${sound.name.replace("~track", "")} ${("0" + timeLeft.toFixed(2)).slice(-5)}`;
                slider.style.opacity = "1.0";
            })
        });

        let mixer = document.getElementById("mixer");
         Array.from(mixer.children).forEach(element => {
            Array.from(element.children).forEach(e => {
                if (e.tagName.toLowerCase() == "input" && e.id.startsWith("snd_")) {
                    let slider = e as HTMLInputElement;
                    slider.disabled = false;
                    let soundName = e.id.substring('snd_'.length);
                    let control = this.getMixerControl(soundName)
                    let isCurrentlyPlaying = this.soundIsPlaying(soundName);
                    let sliderColor = isCurrentlyPlaying ? this.sliderPlayingColor : this.sliderStartColor;
                    let labelColor = isCurrentlyPlaying && Number(slider.value) > 0 ? "white" : mixer.style.color;
                    if (!this.soundExists(soundName)) {
                        sliderColor = "red";
                        labelColor = "red";
                        slider.disabled = true;
                    } else {
                        let source = this.currentSounds.find(x => x.name == soundName);
                        if (source && Utils.isNumeric(App.config.soundVolumeOverride[soundName]) && source.volume() != App.config.soundVolumeOverride[soundName]) {
                            sliderColor = "yellow";
                            labelColor = "yellow";
                            slider.disabled = true;
                        }
                    }

                    this.updateSliderStyle(slider, sliderColor, this.sliderEndColor);
                    e.parentElement.querySelector("label").style.color = labelColor;
                    control.browse.disabled = isCurrentlyPlaying;
                }
            });
         });

        // Updating VU meters
        this.analyserLeft.getByteTimeDomainData(this.bufferLeft);
        this.analyserRight.getByteTimeDomainData(this.bufferRight);

        let leftMax = 0;
        for (let i = 0; i < this.bufferLeft.length; i++) {
            if (this.bufferLeft[i] > leftMax) {
                leftMax = this.bufferLeft[i];
            }
        }

        let rightMax = 0;
        for (let i = 0; i < this.bufferRight.length; i++) {
            if (this.bufferRight[i] > rightMax) {
                rightMax = this.bufferRight[i];
            }
        }

        const valueLeftLinear = (leftMax - 128) / 127 * 100;
        const valueRightLinear = (rightMax - 128) / 127 * 100;

        let valueLeft = valueLeftLinear;
        let valueRight = valueRightLinear;

        if (!this.isLinearVU) {
            // Converting to db
            valueLeft = 20 * this.log10(valueLeftLinear / 100);
            valueRight = 20 * this.log10(valueRightLinear / 100);

            valueLeft = Number.isFinite(valueLeft) ? valueLeft : this.minDecibels;
            valueRight = Number.isFinite(valueRight) ? valueRight : this.minDecibels;
            valueLeft = (valueLeft - this.minDecibels) / this.minDecibels * -100;
            valueRight = (valueRight - this.minDecibels) / this.minDecibels * -100;
        }

        this.vuLeft.style.background = this.getVuBackground(valueLeft);
        this.vuRight.style.background = this.getVuBackground(valueRight);
        const clippingValue = (this.isLinearVU ? this.vuMeterSettings.linear : this.vuMeterSettings.logarithmic).red[0];
        if (valueLeft > clippingValue) {
            this.vuLeft.style.border = "1px dashed red";
        }

        if (valueRight > clippingValue) {
            this.vuRight.style.border = "1px dashed red";
        }
    }

    private getVuBackground(value: number): string {
        const settings = this.isLinearVU ? this.vuMeterSettings.linear : this.vuMeterSettings.logarithmic;
        let stops = [];
        let finalized = false;
        Object.keys(settings).forEach(color => {
            const array = settings[color];
            if (!finalized) {
                const start = array[0];
                const end = array[1];
                if (value > end) {
                    stops.push({color: color , percents: start});
                    stops.push({color: color, percents: end});
                } else {
                    stops.push({color: color, percents: start});
                    stops.push({color: color, percents: value});
                    stops.push({color: "transparent", percents: value});
                    stops.push({color: "transparent", percents: 100});
                    finalized = true;
                }
            }
        });

        let gradient = "linear-gradient(90deg, ";
        stops.forEach(stop => {
            gradient += stop.color + " " + stop.percents + "%, ";
        });

        gradient = gradient.substring(0, gradient.length - 2) + ")";
        return gradient;
    }

    private log10(linear: number) {
        return Math.log(linear) / Math.LN10;
    }

    private updateSliderStyle(slider: HTMLInputElement, sliderStartColor: string, sliderEndColor: string) {
        if (Number(slider.style.opacity) < 1 && !slider.disabled) {
            sliderStartColor = this.sliderInitialColor;
        }

        var sliderValue = (Number(slider.value) - Number(slider.min)) / (Number(slider.max) - Number(slider.min)) * 100;
        slider.style.background = `linear-gradient(to right, ${sliderStartColor} 0%, ${sliderStartColor} ${sliderValue}%, ${sliderEndColor} ${sliderValue}%, white 100%)`;
    }

    private loadSoundOverrides() {
        let mixer = document.getElementById("mixer");
        Array.from(mixer.children).forEach(element => {
           Array.from(element.children).forEach(e => {
               if (e.tagName.toLowerCase() == "input" && e.id.startsWith("snd_")) {
                   let id = e.id.substring("snd_".length);
                   let slider = e as HTMLInputElement;
                   if (Utils.isNumeric(App.config.soundVolumeOverride[id])) {
                       slider.value = App.config.soundVolumeOverride[id].toFixed(2);
                       slider.parentElement.querySelector("span").textContent = App.config.soundVolumeOverride[id].toFixed(2);
                    //    let source = this.getSound(id);
                    //    source.volume(Number(slider.value));
                   }
               }
           });
        });
    }

    private getMixerControl(soundName: string): IMixerControl {
        let slider = document.getElementById("snd_" + soundName) as HTMLInputElement;
        if (!slider) {
            return undefined;
        }

        let parent = slider.parentElement as HTMLDivElement;

        return {
            browse: parent.querySelector("button:first-of-type"),
            play: parent.querySelector("input[button]"),
            slider: slider,
            volume: parent.querySelector("span"),
            name: parent.querySelector("label:last-of-type")
        };
    }

    public isMusic(soundId: string) {
        return this.backgroundMusics.has(soundId);
    }

    private autoFadeSoundTriggers(snd: GameHowl) {
        if (this.autoFadeConfig && this.autoFadeConfig.soundTriggers) {
            Object.entries(this.autoFadeConfig.soundTriggers).forEach(kvp => {
                if (typeof snd.name !== 'string') {
                    return;
                }

                const key = kvp[0];
                let trigger = kvp[1];

                if (!trigger) {
                    return;
                }

                if (!key.includes(snd.name)) {
                    return;
                }

                let soundNames: string[];
                if (key.includes("~")) {
                    // This is a chain of sound names
                    soundNames = key.split("~");
                } else {
                    // This is a single sound name
                    soundNames = [snd.name];
                    // This ensures exact match
                    trigger = this.autoFadeConfig.soundTriggers[snd.name]
                }

                soundNames.forEach(soundName => {
                    if (trigger) {
                        const action = this.triggerToAction(trigger);
                        App.dispatcher.once("AllSoundInstancesUnloaded_" + soundName, () => {
                            action();
                        }, this);
                    }
                })
            });
        }
    }

    private autoFadeStateTriggers(stateName: string) {
        if (this.autoFadeConfig) {
            const trigger = this.autoFadeConfig.stateTriggers[stateName];
            if (trigger) {
                const action = this.triggerToAction(trigger);
                action();
            }
        }
    }

    private autoFadeEventTriggers(eventName: string) {
        if (this.autoFadeConfig) {
            const trigger = this.autoFadeConfig.eventTriggers[eventName];
            if (trigger) {
                const action = this.triggerToAction(trigger);
                action();
            }
        }
    }

    private triggerToAction(trigger: IAutoFadeTrigger): VoidFunction {
        let postActions = new Array<VoidFunction>();
        const result: VoidFunction = () => {
            if (postActions) {
                postActions.forEach(fn => {
                    fn();
                })

                postActions = undefined;
            }
        };

        if (trigger) {
            Object.keys(trigger).forEach(triggerKey => {
                const rule = trigger[triggerKey] as IAutoFadeRule;
                if (!rule.actions) {
                    return;
                }

                if (rule.conditions === undefined) {
                    rule.conditions = ["default"];
                }

                let conditionsMet = this.checkAllRuleConditions(rule);
                if (!conditionsMet) {
                    return;
                }

                rule.actions.forEach(action => {
                    if (action.type === undefined) {
                        action.type = AutoFadeType.fade;
                    }

                    if (action.duration === undefined) {
                        action.duration = 0.5;
                    }

                    if (action.to === undefined) {
                        action.to = 0;
                    }

                    if (action.ids === undefined) {
                        action.ids = new Array<string>();
                        this.backgroundMusics.forEach(v => {
                            action.ids.push(v.name as string);
                        })
                    }

                    let delay = 0;
                    if (action.delay !== undefined && action.delay > 0) {
                        delay = action.delay;
                    }

                    if (action.stop) {
                        const stopAction = () => {
                            this.stopMusic(action.ids);
                        };

                        Tween.delayedCall(action.duration + delay, () => { stopAction() }, [], this);
                    }

                    if (action.restart) {
                        const restartAction = () => {
                            action.ids.forEach(id => {
                                this.stopMusic([id]);
                                this.playMusic(id, 0, false, [], true);
                            });
                        };

                        if (delay > 0) {
                            Tween.delayedCall(delay, () => { restartAction() }, [], this);
                        } else {
                            restartAction();
                        }
                    }

                    switch (action.type) {
                        case AutoFadeType.fade: {
                            this.backgroundMusics.forEach(music => {
                                const musicName = music.name as string;
                                if (!action.ids.includes(musicName)) {
                                    return;
                                }
        
                                let fromVolume = music.snd.volume();
                                if (action.from !== undefined) {
                                    fromVolume = action.from;
                                }
        
                                const newVolume = action.to;
                                const duration = action.duration;
                                const fadeAction = () => {
                                    this.fadeMusic(fromVolume, newVolume, duration, [musicName]);
                                };

                                if (delay > 0) {
                                    Tween.delayedCall(delay, () => { fadeAction() }, [], this);
                                } else {
                                    fadeAction();
                                }
                            })
                        }
                        break;
                        case AutoFadeType.set: {
                                this.backgroundMusics.forEach(music => {
                                    const musicName = music.name as string;
                                    if (!action.ids.includes(musicName)) {
                                        return;
                                    }
            
                                    const setAction = () => {
                                        this.setSpecificMusicVolume(musicName, action.to);
                                    };

                                    if (delay > 0) {
                                        Tween.delayedCall(delay, () => { setAction() }, [], this);
                                    } else {
                                        setAction();
                                    }
                                })
                            }
                            break;
                        case AutoFadeType.yoyo: {
                                this.backgroundMusics.forEach(music => {
                                    const musicName = music.name as string;
                                    if (!action.ids.includes(musicName)) {
                                        return;
                                    }
            
                                    let fromVolume = music.snd.volume();
                                    if (action.from !== undefined) {
                                        fromVolume = action.from;
                                    }
            
                                    const newVolume = action.to;
                                    const duration = action.duration;
                                    Tween.delayedCall(delay, () => {
                                        this.fadeMusic(fromVolume, newVolume, duration, [musicName]);
                                        // Reversing the volumes, once the first fade was made.
                                        action.from = fromVolume;
                                        action.to = newVolume;
                                    }, [], this);
                                    
                                    postActions.push(() => {
                                        Tween.delayedCall(delay, () => {
                                            const postConditionsMet = this.checkAllRuleConditions(rule);
                                            if (postConditionsMet) {
                                                this.fadeMusic(action.to, action.from, duration, [musicName]);
                                            }
                                        }, [], this);
                                    })
                                })
                            }
                            break;
                    }
                });
            });
        }

        return result;
    }

    private checkAllRuleConditions(rule: IAutoFadeRule): boolean {
        let conditionsMet = true;
        rule.conditions.forEach(name => {
            let conditionName = name;
            let reversed = false;
            if (conditionName.startsWith("!")) {
                conditionName = conditionName.substring(1);
                reversed = true;
            }

            let condition = StateMachine.getCondition(conditionName);
            let conditionResult = condition.check();
            if (reversed == false && conditionResult != true ||
                reversed == true && conditionResult != false) {
                conditionsMet = false;
            }
        });

        return conditionsMet;
    }

    private initFadeSettings() {
        this.autoFadeConfig = App.config.fadeSettings as IAutoFadeConfiguration;
        if (this.autoFadeConfig) {
            if (this.autoFadeConfig.stateTriggers) {
                App.dispatcher.on(GameEvents.STATE_CHANGED, (enterState: string, exitState: string) => {
                    this.autoFadeStateTriggers(enterState);
                }, this);
            }

            if (this.autoFadeConfig.eventTriggers) {
                Object.keys(this.autoFadeConfig.eventTriggers).forEach(eventName => {
                    App.dispatcher.on(eventName, () => {
                        this.autoFadeEventTriggers(eventName);
                    }, this);
                });
            }
        }
    }
}

interface IMixerControl {
    browse: HTMLButtonElement,
    play: HTMLInputElement,
    slider: HTMLInputElement,
    volume: HTMLSpanElement,
    name: HTMLLabelElement
}

interface ISoundBlob {
    url: string;
    extension: string;
}

interface IAutoFadeRule {
    conditions: string[];
    actions: IAutoFadeAction[];
}

interface IAutoFadeAction {
    to: number;
    duration: number;
    from?: number;
    ids?: string[];
    type?: AutoFadeType;
    delay?: number;
    restart?: boolean;
    stop?: boolean;
}

interface IAutoFadeTrigger {
    rules: IAutoFadeRule[];
}

interface IAutoFadeConfiguration {
    stateTriggers: IAutoFadeTrigger[];
    soundTriggers: IAutoFadeTrigger[];
    eventTriggers: IAutoFadeTrigger[];
}

enum AutoFadeType {
    fade = "fade",
    yoyo = "yoyo",
    set = "set"
}

export default new SoundController();
